import { useState, useCallback, useMemo } from "react";
import { LucideIcon ,Button} from "./ot";

// ─── Color utils ───────────────────────────────────────────────────────────────

const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16),
    ];
};

const mixWhite = (rgb, amount) =>
    rgb.map((c) => Math.round(c + (255 - c) * amount));

const mixBlack = (rgb, amount) =>
    rgb.map((c) => Math.round(c * (1 - amount)));

const toRgbString = ([r, g, b]) => `rgb(${r},${g},${b})`;
const toRgbaString = ([r, g, b], a) => `rgba(${r},${g},${b},${a})`;

/**
 * Derive all CSS variable values from a single hex primary color.
 *
 * --cp-icon-bg       icon wrapper background  (primary + 92% white)
 * --cp-icon-text     icon color               (primary)
 * --cp-desc-text     description text         (primary)
 * --cp-grid-border   outer grid border        (primary + 80% white)
 * --cp-grid-bg       outer grid background    (primary + 95% white, 40% opacity)
 * --cp-btn-border    AI button border         (primary + 60% white)
 * --cp-btn-text      AI button text           (primary + 10% black)
 * --cp-btn-hover-bg  AI button hover bg       (primary + 95% white)
 */
const deriveColorVars = (hex) => {
    const rgb = hexToRgb(hex);
    return {
        "--cp-icon-bg": toRgbString(mixWhite(rgb, 0.92)),
        "--cp-icon-text": toRgbString(rgb),
        "--cp-desc-text": toRgbString(rgb),
        "--cp-grid-border": toRgbString(mixWhite(rgb, 0.80)),
        "--cp-grid-bg": toRgbaString(mixWhite(rgb, 0.95), 0.4),
        "--cp-btn-border": toRgbString(mixWhite(rgb, 0.60)),
        "--cp-btn-text": toRgbString(mixBlack(rgb, 0.10)),
        "--cp-btn-hover-bg": toRgbString(mixWhite(rgb, 0.95)),
    };
};

const DEFAULT_PRIMARY_COLOR = "#3b82f6";

// ─── Data normalizer ───────────────────────────────────────────────────────────

/**
 * Parse lucide icon name from the icon string stored in the API.
 * e.g. `<Heading1 className="w-4 h-4" />` → "Heading1"
 *      `<ImageIcon className=\"w-4 h-4\" />` → "ImageIcon"
 * Falls back to "Box" if parsing fails.
 */
const parseIconName = (iconString = "") => {
    const match = iconString.match(/<([A-Za-z0-9]+)[\s/]/);
    return match ? match[1] : "Box";
};

/**
 * Normalize raw API item into the shape ComponentCard needs.
 *
 * Raw API shape:
 * {
 *   component_definitions_id: number,           ← pk
 *   component_definitions_name: string,
 *   component_definitions_classify_structure_type_data: [{
 *     component_type: string,
 *     icon: string,                             ← e.g. "<Heading1 className=\"w-4 h-4\" />"
 *     description: string,
 *   }, ...]
 * }
 */
const normalizeItem = (raw) => {
    const def = raw.component_definitions_classify_structure_type_data?.[0] ?? {};
    return {
        id: raw.component_definitions_id,
        componentType: def.component_type ?? "",
        name: raw.component_definitions_name ?? raw.name ?? "",
        desc: def.description ?? "",
        iconName: parseIconName(def.icon),
    };
};

// ─── ComponentCard ─────────────────────────────────────────────────────────────

const ComponentCard = ({ item, count, onCountChange }) => (
    <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm hover:shadow-md transition-shadow duration-200">
        {/* Icon */}
        <div
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--cp-icon-bg)" }}
        >
            <LucideIcon
                name={item.iconName}
                className="w-4 h-4"
                style={{ color: "var(--cp-icon-text)" }}
            />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 leading-tight truncate">
                {item.name}
            </p>
            <p className="text-xs text-gray-400 truncate leading-tight mt-0.5">
                {item.desc}
            </p>
        </div>

        {/* Counter */}
        <div className="flex items-center gap-2 flex-shrink-0">
            <button
                onClick={() => onCountChange(item.id, -1)}
                disabled={count === 0}
                className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 flex items-center justify-center text-base font-medium hover:bg-gray-50 hover:border-gray-300 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 select-none"
                aria-label={`減少 ${item.name}`}
            >
                −
            </button>
            <span
                className={`w-6 text-center text-base font-semibold tabular-nums ${count === 0 ? "text-gray-300" : "text-gray-800"
                    }`}
            >
                {count}
            </span>
            <button
                onClick={() => onCountChange(item.id, 1)}
                className="w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 flex items-center justify-center text-base font-medium hover:bg-gray-50 hover:border-gray-300 active:scale-95 transition-all duration-150 select-none"
                aria-label={`增加 ${item.name}`}
            >
                +
            </button>
        </div>
    </div>
);

// ─── ComponentQuantityPanel ────────────────────────────────────────────────────
/**
 * Props:
 *   componentDefinitions : RawApiItem[]            — required, raw API data array
 *   initialCounts?       : Record<number, number>  — key: component_definitions_id
 *   counts?              : Record<number, number>  — controlled mode (pair with onChange)
 *   onChange?            : (counts: Record<number, number>) => void
 *   onAISuggest?         : () => void
 *   title?               : string
 *   description?         : string
 *   primaryColor?        : string                  — hex color, e.g. "#3b82f6"
 *   className?           : string
 */
const ComponentQuantityPanel = ({
    loading = false,
    componentDefinitions = [],
    initialCounts,
    counts: controlledCounts,
    onChange,
    onAISuggest,
    title = "設定元件數量",
    description = "設定每種元件的數量，AI 將根據您的設定生成對應的圖框樣式。",
    primaryColor = DEFAULT_PRIMARY_COLOR,
    className = "",
}) => {
    const colorVars = useMemo(() => deriveColorVars(primaryColor), [primaryColor]);

    // Normalize raw API data once
    const items = useMemo(
        () => componentDefinitions.map(normalizeItem),
        [componentDefinitions]
    );

    const isControlled = controlledCounts !== undefined;

    const [internalCounts, setInternalCounts] = useState(() => {
        const defaults = Object.fromEntries(items.map((d) => [d.id, 0]));
        return initialCounts ? { ...defaults, ...initialCounts } : defaults;
    });

    const counts = isControlled ? controlledCounts : internalCounts;

    const handleCountChange = useCallback(
        (id, delta) => {
            const next = {
                ...counts,
                [id]: Math.max(0, (counts[id] ?? 0) + delta),
            };
            if (!isControlled) setInternalCounts(next);
            onChange?.(next);
        },
        [counts, isControlled, onChange]
    );

    return (
        <div
            className={`w-full font-sans ${className}`}
            style={colorVars}
            role="region"
            aria-label={title}
        >
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <Button
                loading={loading}
                    onClick={onAISuggest}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white active:scale-95 transition-all duration-150 shadow-sm"
                    style={{
                        border: "1.5px solid var(--cp-btn-border)",
                        color: "var(--cp-btn-text)",
                    }}
                    onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "var(--cp-btn-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                    }
                >
                    <LucideIcon name="Sparkles" className="w-3.5 h-3.5" />
                    AI元件數量建議
                </Button>
            </div>

            {/* Description */}
            <p className="text-sm mb-4" style={{ color: "var(--cp-desc-text)" }}>
                {description}
            </p>

            {/* Grid */}
            <div
                className="rounded-2xl p-3"
                style={{
                    border: "1.5px solid var(--cp-grid-border)",
                    backgroundColor: "var(--cp-grid-bg)",
                }}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {items.map((item) => (
                        <ComponentCard
                            key={item.id}
                            item={item}
                            count={counts[item.id] ?? 0}
                            onCountChange={handleCountChange}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ComponentQuantityPanel;
export { normalizeItem, parseIconName };